import React from 'react';
import { getIntl } from '../../../utils/intl';
import { APITypes } from '../../../primary-record-types';
import getDateString from '../../../utils/getDateString';
import SearchInput from '../../v2BaseComponents/SearchInput';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import ProfileMultiSelect, { ProfileSelectOption } from '../../v2BaseComponents/Inputs/ProfileMultiSelect';
import getName from '../../../utils/getName';
import { ProfileSelectionState, isProfileListItem } from './types';

const intl = getIntl('LogSearch', {
    placeholder: 'Search Recent Activity',
    filterByProfile: 'Filter by Profile',
});

export type LogSearchOwnProps = {
    searchText: string;
    onChange: (value: string) => void;
    disableSelection?: boolean;
    searchTextPlaceholder?: string;
} & ProfileSelectionState;

export type LogSearchProps = LogSearchOwnProps;

export const applySearchFilter = (
    logs: APITypes.auditLogService.AuditLogListItem[],
    filterValue: string,
): APITypes.auditLogService.AuditLogListItem[] => {
    const withMessages = logs.map((_) => ({
        ..._,
        message: _.auditLogTitle,
        date: getDateString(new Date(_.timestamp).toISOString(), 'date-time'),
    }));

    return withMessages.filter((_) => {
        const messageFormatted = _.message.toLowerCase();
        const dateFormatted = _.date.toLowerCase();

        return (
            messageFormatted.includes(filterValue.toLowerCase()) || dateFormatted.includes(filterValue.toLowerCase())
        );
    });
};

const LogSearch: React.FC<LogSearchProps> = ({
    searchText,
    onChange,
    disableSelection,
    profiles,
    selectedProfileIds,
    onSetProfileIds,
    searchTextPlaceholder,
}) => {
    const selectOptions = React.useMemo<ProfileSelectOption[]>(
        () =>
            profiles.map((_) => {
                const name = isProfileListItem(_) ? getName(_) : getName(_.contactDetails);
                return {
                    label: name,
                    value: _.medicalProfileId,
                    testID: name,
                    avatarUrl: _.avatarUrl,
                };
            }),
        [profiles],
    );

    return (
        <>
            <Stack vertical fullWidth align="center" space={contentStackSpacing}>
                <SearchInput
                    style={{ width: '100%' }}
                    value={searchText}
                    onChange={onChange}
                    placeholder={searchTextPlaceholder || intl('placeholder')}
                    testID="log-search-input"
                />
                {!disableSelection ? (
                    <ProfileMultiSelect
                        value={selectedProfileIds}
                        onChange={onSetProfileIds}
                        selectOptions={selectOptions}
                        title={intl('filterByProfile')}
                        testID="log-search-profile-select"
                    />
                ) : null}
            </Stack>
        </>
    );
};

export default React.memo(LogSearch);
